// client/legend-cms/src/worldCupMatches/WorldCupMatchEdit.js
import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  DateTimeInput,
  NumberInput,
  SelectInput,
  ReferenceInput,
  DeleteButton,
  required,
  minValue,
  Toolbar,
  SaveButton,
  useNotify,
  useRefresh,
  useRedirect,
  useRecordContext,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles({
  inline: {
    display: 'inline-block',
    marginLeft: '1em',
    '&.first-child': {
      marginLeft: 0,
    },
  },
  finishButton: {
    marginLeft: '1em',
    backgroundColor: '#00F5A8',
    color: '#000',
    fontWeight: 800,
    '&:hover': {
      backgroundColor: '#00d994',
    },
  },
  toolbarContainer: {
    width: '100%',
  },
  helperText: {
    width: '100%',
    marginBottom: 12,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#fff8e1',
    color: '#5d4037',
    fontSize: 13,
    fontWeight: 600,
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});

const statusChoices = [
  { id: 'SCHEDULED', name: 'Agendado' },
  { id: 'OPEN', name: 'Aberto para palpites' },
  { id: 'CLOSED', name: 'Fechado para palpites' },
];

const getAuthHeaders = () => {
  const { access_token } = JSON.parse(
    localStorage.getItem('auth') || '{"access_token": ""}',
  );

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${access_token}`,
  };
};

const FinishMatchButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [homeScore, setHomeScore] = React.useState('');
  const [awayScore, setAwayScore] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  if (!record || record.status === 'FINISHED') {
    return null;
  }

  const handleOpen = () => {
    setHomeScore('');
    setAwayScore('');
    setOpen(true);
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
    }
  };

  const handleFinish = async () => {
    const parsedHomeScore = Number(homeScore);
    const parsedAwayScore = Number(awayScore);

    if (
      Number.isNaN(parsedHomeScore) ||
      Number.isNaN(parsedAwayScore) ||
      parsedHomeScore < 0 ||
      parsedAwayScore < 0
    ) {
      notify('Informe um placar válido para finalizar a partida.', 'warning');
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja finalizar esta partida? Essa ação vai processar a XP dos palpites e não deve ser repetida.',
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/cms/world-cup-matches/${record.id}/finish`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            home_score: parsedHomeScore,
            away_score: parsedAwayScore,
          }),
        },
      );

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.message || 'Erro ao finalizar partida');
      }

      notify(
        `Partida finalizada. Palpites processados: ${json.data?.processed_predictions ?? 0}`,
        'success',
      );

      setOpen(false);
      refresh();
      redirect('/world-cup-matches');
    } catch (error) {
      notify(error.message || 'Erro ao finalizar partida', 'warning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        className={classes.finishButton}
        variant="contained"
        onClick={handleOpen}
        disabled={loading}
      >
        Finalizar partida
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Finalizar partida</DialogTitle>

        <DialogContent>
          <TextField
            label={record.home_team}
            type="number"
            value={homeScore}
            onChange={(event) => setHomeScore(event.target.value)}
            margin="normal"
            fullWidth
          />

          <TextField
            label={record.away_team}
            type="number"
            value={awayScore}
            onChange={(event) => setAwayScore(event.target.value)}
            margin="normal"
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>

          <Button
            onClick={handleFinish}
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: '#00F5A8',
              color: '#000',
              fontWeight: 800,
            }}
          >
            Confirmar e processar XP
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomToolbar = (props) => {
  const classes = useStyles();
  const record = useRecordContext();

  return (
    <Toolbar {...props}>
      <div className={classes.toolbarContainer}>
        {record?.status === 'FINISHED' ? (
          <div
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Esta partida foi finalizada. Os palpites foram processados e a XP
            foi distribuída. Não é possível editar uma partida finalizada.
          </div>
        ) : (
          <>
            <div className={classes.helperText}>
              Atenção: a XP dos palpites só será contabilizada ao utilizar o
              botão “Finalizar partida”. O botão “Salvar” apenas atualiza os
              dados da partida e não processa os palpites.
            </div>

            <div className={classes.toolbarActions}>
              <SaveButton {...props} />

              <FinishMatchButton />

              <DeleteButton
                mutationMode="pessimistic"
                confirmTitle="Excluir partida"
                confirmContent="Tem certeza que deseja excluir esta partida? Esta ação não deve ser usada para finalizar jogos."
              />
            </div>
          </>
        )}
      </div>
    </Toolbar>
  );
};

const WorldCupMatchEdit = (props) => {
  const classes = useStyles();

  return (
    <Edit title="Editar jogo da Copa" {...props}>
      <SimpleForm toolbar={<CustomToolbar />}>
        <ReferenceInput
          source="campaign_id"
          reference="prediction-campaigns"
          label="Campanha"
          sort={{ field: 'id', order: 'DESC' }}
          fullWidth
        >
          <SelectInput optionText="title" />
        </ReferenceInput>

        <div />

        <TextInput
          source="title"
          label="Título"
          helperText="Se vazio, será gerado automaticamente. Ex: Brasil x Argentina"
          fullWidth
        />

        <ReferenceInput
          source="home_team_id"
          reference="prediction-teams"
          label="Time mandante"
          filter={{ active: true }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={500}
          validate={required()}
          formClassName={clsx(classes.inline, 'first-child')}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <ReferenceInput
          source="away_team_id"
          reference="prediction-teams"
          label="Time visitante"
          filter={{ active: true }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={500}
          validate={required()}
          formClassName={classes.inline}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <div />

        <DateTimeInput
          source="match_date"
          label="Data e hora do jogo"
          validate={required()}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <DateTimeInput
          source="prediction_deadline"
          label="Limite para palpites"
          formClassName={classes.inline}
        />

        <div />

        <SelectInput
          source="status"
          label="Status"
          choices={statusChoices}
          validate={required()}
        />

        <div />

        <NumberInput
          source="home_score"
          label="Gols mandante"
          validate={minValue(0)}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <NumberInput
          source="away_score"
          label="Gols visitante"
          validate={minValue(0)}
          formClassName={classes.inline}
        />

        <div />

        <NumberInput
          source="exact_score_xp"
          label="XP por placar exato"
          validate={[required(), minValue(0)]}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <NumberInput
          source="correct_winner_xp"
          label="XP por vencedor/empate correto"
          validate={[required(), minValue(0)]}
          formClassName={classes.inline}
        />

        <NumberInput
          source="participation_xp"
          label="XP por participação"
          validate={[required(), minValue(0)]}
          formClassName={classes.inline}
        />
      </SimpleForm>
    </Edit>
  );
};

export default WorldCupMatchEdit;
